import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { useForm } from "react-hook-form";
import useAuth from "../../../../hooks/useAuth";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { Copy } from "../../../atoms/Copy";
import { useGetEntityUser, useUserSendMessage } from "../../../../hooks/users";
import { usePermissions, PlanTier } from "../../../../utils/permissions";
import { FiHelpCircle } from "react-icons/fi";
import { useState, useEffect, useMemo } from "react";
import ReactSelect from "react-select";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";
import BusinessCardFileUpload from "../../../atoms/BusinessCardFileUpload";
import { PlansModal } from "../../../modals/PlansModal";
import { useSearchParams } from "react-router-dom";

const schema = Joi.object({
  requestType: Joi.string().required().label("Request Type"),
  message: Joi.string().trim().min(5).max(3500).required().label("Message"),
});

const BORDURE_SELECT = {
  // design-ok: jeton red-500, style JS de react-select, pas de classe possible
  erreur: "#EF4444",
  // design-ok: jeton blue-500, style JS de react-select, pas de classe possible
  focus: "#3B82F6",
  // design-ok: jeton gray-300, style JS de react-select, pas de classe possible
  repos: "#D1D5DB",
  // design-ok: jeton gray-400, style JS de react-select, pas de classe possible
  survol: "#9CA3AF",
};

const AskQuestionMessage = () => {
  const [t] = useTranslation();
  const [searchParams] = useSearchParams();
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [selectedRequestType, setSelectedRequestType] = useState<any>(null);
  const [attachments, setAttachments] = useState<
    Array<{
      fileName: string;
      cdnPath: string;
      displayName: string;
    }>
  >([]);

  const {
    customer_identification,
    user,
    user: { entity_identification: entityIdentification },
  } = useAuth();
  const permissions = usePermissions(user);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: joiResolver(schema),
  });

  const { isSuccess, data: res } = useGetEntityUser({
    customer_identification: customer_identification,
    entityIdentification: entityIdentification,
    user_identification: customer_identification,
  });
  const { mutate: sendMessage, isLoading } = useUserSendMessage();

  const requestTypeOptions = useMemo(
    () => [
      {
        value: "technical_issue",
        label: t("dashboard.message.request_types.technical_issue"),
      },
      {
        value: "billing_question",
        label: t("dashboard.message.request_types.billing_question"),
      },
      {
        value: "feature_request",
        label: t("dashboard.message.request_types.feature_request"),
      },
      {
        value: "account_help",
        label: t("dashboard.message.request_types.account_help"),
      },
      {
        value: "general_inquiry",
        label: t("dashboard.message.request_types.general_inquiry"),
      },
      {
        value: "bug_report",
        label: t("dashboard.message.request_types.bug_report"),
      },
      {
        value: "large_enterprise_request",
        label: t("dashboard.message.request_types.large_enterprise_request"),
      },
      {
        value: "whatsapp_setup",
        label: t("dashboard.message.request_types.whatsapp_setup"),
      },
      { value: "other", label: t("dashboard.message.request_types.other") },
    ],
    [t]
  );

  useEffect(() => {
    const requestType = searchParams.get("requestType");
    if (!requestType) return;
    const matchedOption = requestTypeOptions.find(
      (option) => option.value === requestType
    );
    if (matchedOption) {
      setSelectedRequestType(matchedOption);
      setValue("requestType", matchedOption.value);
    }
  }, [searchParams, setValue, requestTypeOptions]);

  useEffect(() => {
    if (selectedRequestType) {
      const updatedOption = requestTypeOptions.find(
        (option) => option.value === selectedRequestType.value
      );
      if (updatedOption && updatedOption.label !== selectedRequestType.label) {
        setSelectedRequestType(updatedOption);
      }
    }
  }, [requestTypeOptions]);

  const onSubmit = (values: any) => {
    if (isSuccess) {
      let messageWithContext = `[Request Type: ${
        selectedRequestType?.label || "Not specified"
      }]\n[User ID: ${customer_identification}]\n\n${values.message}`;

      if (attachments?.length > 0) {
        messageWithContext = messageWithContext + "\nAttachments: \n";
        attachments.map((item) => {
          messageWithContext = messageWithContext + item.cdnPath + "\n";
        });
      }

      sendMessage(
        {
          customer_identification: customer_identification,
          entityIdentification: entityIdentification,
          data: {
            message: messageWithContext,
            customer_email: res?.user_email,
          },
        },
        {
          onSuccess: () => {
            reset({
              requestType: "",
              message: "",
            });
            setAttachments([]);
            setSelectedRequestType(null);
          },
        }
      );
    }
  };

  return (
    <div className="message-page">
      <Helmet>
        <title>{t("dashboard.message.page_title")} | Super User Manager</title>
        <meta
          name="description"
          content={t("dashboard.message.page_description") as string}
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/messages`}
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="flex items-center gap-3 mb-2">
          <FiHelpCircle className="w-7 h-7 text-blue-600" />
          <h1 className="text-gray-700 text-2xl font-bold font-figtree">
            {t("dashboard.message.page_title")}
          </h1>
        </div>
        <p className="text-gray-600 text-sm font-normal font-figtree mb-6">
          {t("dashboard.message.page_description")}
        </p>

        <div className="w-full p-6 bg-white rounded-lg shadow">
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-gray-600 text-xs font-normal font-figtree mb-3">
              {t("dashboard.message.user_identification_help")}
            </p>
            <div className="flex flex-col gap-3 sm:gap-2">
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <label className="shrink-0 text-sm font-medium text-gray-700">
                  {t("dashboard.message.entity_identification_label")} :
                </label>
                <code className="min-w-0 px-3 py-2 bg-white border border-gray-300 rounded text-sm font-mono text-gray-800 inline-flex items-center">
                  <Copy target={entityIdentification} truncate={false} />
                </code>
              </div>
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <label className="shrink-0 text-sm font-medium text-gray-700">
                  {t("dashboard.message.user_identification_label")} :
                </label>
                <code className="min-w-0 px-3 py-2 bg-white border border-gray-300 rounded text-sm font-mono text-gray-800 inline-flex items-center">
                  <Copy target={customer_identification} truncate={false} />
                </code>
              </div>
            </div>
          </div>

          <h2 className="text-gray-700 text-lg font-semibold font-figtree leading-7 mb-2">
            {t("dashboard.message.title")}
          </h2>
          <p className="text-gray-600 text-sm font-normal font-figtree leading-tight mb-5">
            {t("dashboard.message.text-02")}
          </p>

          {permissions.isPlanTier(PlanTier.FREE) && (
            <div className="mb-5">
              <FeatureUpgradePrompt
                title={t("dashboard.message.free_user_alert_title") as string}
                description={
                  t("dashboard.message.free_user_alert_description") as string
                }
                benefits={[
                  t("dashboard.message.premium_benefit_1") as string,
                  t("dashboard.message.premium_benefit_2") as string,
                ]}
                upgradeText={t("dashboard.message.upgrade_plan_btn") as string}
                onUpgrade={() => setShowPlansModal(true)}
                iconTheme="blue"
                className="w-full"
              />
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="w-full">
            <div className="mb-5">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("dashboard.message.request_type_label")}
              </label>
              <div translate="no">
                <ReactSelect
                  options={requestTypeOptions}
                  value={selectedRequestType}
                  onChange={(selected) => {
                    setSelectedRequestType(selected);
                    setValue("requestType", selected?.value || "");
                  }}
                  placeholder={t("dashboard.message.request_type_placeholder")}
                  className="w-full"
                  classNamePrefix="react-select"
                  isSearchable={false}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: errors.requestType
                        ? BORDURE_SELECT.erreur
                        : state.isFocused
                        ? BORDURE_SELECT.focus
                        : BORDURE_SELECT.repos,
                      boxShadow: state.isFocused
                        ? `0 0 0 1px ${BORDURE_SELECT.focus}`
                        : "none",
                      "&:hover": {
                        borderColor: state.isFocused
                          ? BORDURE_SELECT.focus
                          : BORDURE_SELECT.survol,
                      },
                    }),
                  }}
                />
                {errors.requestType && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.requestType.message?.toString()}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-5">
              <CustomInput
                rows={6}
                name="message"
                control={control}
                error={errors.message}
                helperText={errors.message?.message}
                placeholder={t("dashboard.message.placeholder") as string}
                label={t("dashboard.message.label") as string}
              />
            </div>

            <div className="grid gap-4">
              {!permissions.isPlanTier(PlanTier.FREE) && (
                <div className="bg-white border border-amber-300 rounded-lg">
                  <BusinessCardFileUpload
                    onFileUploaded={(fileName, cdnPath, displayName) => {
                      const newFiles = [
                        ...(attachments || []),
                        { fileName, cdnPath, displayName },
                      ];
                      setAttachments(newFiles);
                    }}
                    onFileRemoved={(fileName) => {
                      const newFiles =
                        attachments?.filter(
                          (file) => file.fileName !== fileName
                        ) || [];
                      setAttachments(newFiles);
                    }}
                    uploadedFiles={attachments || []}
                    maxFiles={3}
                    maxFileSizeMB={10}
                    usage="askQuestion"
                  />
                </div>
              )}
            </div>

            <div className="h-px bg-gray-300 my-5" />

            <div className="flex justify-end">
              <CustomButton
                type="submit"
                disabled={isLoading || !selectedRequestType}
                isLoading={isLoading}
                variant="primary"
                title={t("dashboard.message.btn")}
              />
            </div>
          </form>
        </div>
      </div>

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        targetPlan={PlanTier.PREMIUM}
      />
    </div>
  );
};

export default AskQuestionMessage;
